import { useCallback, useEffect, useRef } from 'react';
import { LogOut, Home, PanelLeftClose, PanelLeftOpen, Sparkles, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import {
  adminRoutes,
  getCategoryByPath,
  getCustomizedAccessibleCategoryIds,
} from '../navigation/adminRoutes';
import { useAdminNavigation } from '../navigation/useAdminNavigation';
import { useWhatsNew } from '../release-notes';

import AdminCockpitNavItem from './AdminCockpitNavItem';
import { getAdminShellGeometry } from './adminShellGeometry';

import { Button } from '@/components/ui/button';
import { CDN_URL } from '@/config/cdn';
import { cn } from '@/lib/utils';

const hasFineHoverPointer = () => (
  typeof window.matchMedia !== 'function'
  || window.matchMedia('(hover: hover) and (pointer: fine)')?.matches !== false
);

export const RAIL_HOVER_EXPAND_DELAY_MS = 650;
export const RAIL_COLLAPSE_DELAY_MS = 120;

const AdminSidebar = ({ onLogout, canAccessSection, overlay = false, onDismiss }) => {
  const location = useLocation();
  const sidebarRef = useRef(null);
  const railControlRef = useRef(null);
  const previousPathRef = useRef(location.pathname);
  const railExpandTimerRef = useRef(null);
  const railCollapseTimerRef = useRef(null);
  const outsidePressRef = useRef(false);
  const {
    navigationPreferences = {},
    railExpanded,
    setRailExpanded,
  } = useAdminNavigation();
  const mode = overlay ? 'overlay' : (navigationPreferences.sidebar_mode || 'rail');
  const { hasUnread, openWhatsNew } = useWhatsNew();
  const expanded = mode !== 'rail' || railExpanded;
  const shellGeometry = getAdminShellGeometry(mode, railExpanded);

  const visibleCategories = getCustomizedAccessibleCategoryIds(
    canAccessSection,
    navigationPreferences,
  );
  const activeCategoryId = getCategoryByPath(location.pathname);

  const clearRailExpand = useCallback(() => {
    if (railExpandTimerRef.current === null) return;
    window.clearTimeout(railExpandTimerRef.current);
    railExpandTimerRef.current = null;
  }, []);

  const clearRailCollapse = useCallback(() => {
    if (railCollapseTimerRef.current === null) return;
    window.clearTimeout(railCollapseTimerRef.current);
    railCollapseTimerRef.current = null;
  }, []);

  const clearRailTimers = useCallback(() => {
    clearRailExpand();
    clearRailCollapse();
  }, [clearRailCollapse, clearRailExpand]);

  const expandRail = useCallback(() => {
    clearRailTimers();
    if (mode === 'rail') setRailExpanded(true);
  }, [clearRailTimers, mode, setRailExpanded]);

  const collapseRail = useCallback(() => {
    clearRailTimers();
    if (mode === 'rail') setRailExpanded(false);
  }, [clearRailTimers, mode, setRailExpanded]);

  const scheduleRailExpand = useCallback(() => {
    if (mode !== 'rail' || railExpanded) return;
    clearRailTimers();
    railExpandTimerRef.current = window.setTimeout(() => {
      railExpandTimerRef.current = null;
      setRailExpanded(true);
    }, RAIL_HOVER_EXPAND_DELAY_MS);
  }, [clearRailTimers, mode, railExpanded, setRailExpanded]);

  const scheduleRailCollapse = useCallback(() => {
    clearRailExpand();
    clearRailCollapse();
    if (mode !== 'rail' || !railExpanded) return;
    railCollapseTimerRef.current = window.setTimeout(() => {
      railCollapseTimerRef.current = null;
      setRailExpanded(false);
    }, RAIL_COLLAPSE_DELAY_MS);
  }, [clearRailCollapse, clearRailExpand, mode, railExpanded, setRailExpanded]);

  useEffect(() => {
    if (previousPathRef.current !== location.pathname) {
      clearRailTimers();
      setRailExpanded(false);
      if (overlay) onDismiss?.();
      previousPathRef.current = location.pathname;
    }
  // Route changes dismiss temporary navigation without making callbacks dependencies.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearRailTimers, location.pathname, overlay, setRailExpanded]);

  useEffect(() => {
    clearRailTimers();
    if (mode !== 'rail') setRailExpanded(false);
  }, [clearRailTimers, mode, setRailExpanded]);

  useEffect(() => () => clearRailTimers(), [clearRailTimers]);

  useEffect(() => {
    if (mode !== 'rail' || !railExpanded) return undefined;
    const handleOutsidePress = (event) => {
      if (sidebarRef.current?.contains(event.target)) return;
      // A hover-leave collapse may already be pending; it would fire mid-press.
      clearRailCollapse();
      outsidePressRef.current = true;
    };
    const handleOutsideIntent = (event) => {
      outsidePressRef.current = false;
      if (!sidebarRef.current?.contains(event.target)) collapseRail();
    };
    // A press that never completes as a click (drag-off, cancel) must not
    // leave focus-out dismissal suppressed; this runs after any click.
    const releaseOutsidePress = () => {
      window.setTimeout(() => { outsidePressRef.current = false; }, 0);
    };
    // Dismiss on the completed click, not pointerdown/touchstart: collapsing
    // shifts the in-flow header and content, so collapsing mid-press would
    // move the pressed control before release and swallow or misroute the click.
    document.addEventListener('pointerdown', handleOutsidePress, true);
    document.addEventListener('click', handleOutsideIntent, true);
    document.addEventListener('pointerup', releaseOutsidePress, true);
    document.addEventListener('pointercancel', releaseOutsidePress, true);
    return () => {
      outsidePressRef.current = false;
      document.removeEventListener('pointerdown', handleOutsidePress, true);
      document.removeEventListener('click', handleOutsideIntent, true);
      document.removeEventListener('pointerup', releaseOutsidePress, true);
      document.removeEventListener('pointercancel', releaseOutsidePress, true);
    };
  }, [clearRailCollapse, collapseRail, mode, railExpanded]);

  useEffect(() => {
    if (mode !== 'rail' || !railExpanded) return undefined;
    const handleEscape = (event) => {
      if (event.key !== 'Escape') return;
      collapseRail();
      railControlRef.current?.focus();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [collapseRail, mode, railExpanded]);

  const collapseAfterFocusLeaves = (event) => {
    // An outside press moves focus on pointerdown; the completed click
    // dismisses the Rail instead, so the pressed control stays put.
    if (outsidePressRef.current) return;
    if (mode === 'rail' && !event.currentTarget.contains(event.relatedTarget)) {
      scheduleRailCollapse();
    }
  };

  return (
    <aside
      ref={sidebarRef}
      className={cn(
        'admin-sidebar fixed left-0 top-0 z-40 flex h-full flex-col overflow-hidden transition-[width] duration-200 ease-out motion-reduce:transition-none',
        shellGeometry.sidebarWidthClass,
      )}
      aria-label="Admin sidebar"
      role={overlay ? 'dialog' : undefined}
      aria-modal={overlay ? 'true' : undefined}
      onMouseEnter={() => {
        if (mode === 'rail' && hasFineHoverPointer()) {
          clearRailCollapse();
          scheduleRailExpand();
        }
      }}
      onMouseLeave={() => {
        // A hover dwell that never completed must not expand after leaving,
        // even when retained focus keeps an expanded Rail open.
        clearRailExpand();
        if (
          mode === 'rail'
          && hasFineHoverPointer()
          && !sidebarRef.current?.contains(document.activeElement)
        ) {
          scheduleRailCollapse();
        }
      }}
      onFocusCapture={(event) => {
        if (mode === 'rail' && event.target !== railControlRef.current) {
          expandRail();
        }
      }}
      onBlurCapture={collapseAfterFocusLeaves}
    >
      <div className="flex h-16 min-h-16 shrink-0 items-center border-b border-stone-300 px-[18px] dark:border-amber-600/30">
        <Link
          to="/admin"
          className="grid h-full min-w-0 flex-1 grid-cols-[52px_minmax(0,1fr)] items-center overflow-hidden rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
          aria-label="Admin dashboard"
          title="Admin dashboard"
          onClick={overlay ? onDismiss : undefined}
          data-header-target="dashboard"
        >
          <span className="flex h-[44px] w-[52px] shrink-0 items-center justify-center">
            <span className="h-10 w-10 overflow-hidden rounded-full border-2 border-amber-600 dark:border-amber-500">
              <img
                src={`${CDN_URL}/images/owaza-logo.jpg`}
                alt="Owaza"
                className="h-full w-full object-cover"
              />
            </span>
          </span>
          <span
            data-navigation-label
            className={cn(
              'min-w-0 whitespace-nowrap pl-3 pr-2 font-semibold text-stone-800 dark:text-white',
              !expanded && 'invisible',
            )}
            aria-hidden={!expanded ? 'true' : undefined}
          >
            Owaza Admin
          </span>
        </Link>
        {overlay && (
          <Button
            variant="ghost"
            size="icon"
            className="h-[44px] w-[44px]"
            onClick={onDismiss}
            aria-label="Close navigation"
          >
            <X className="h-5 w-5" />
          </Button>
        )}
      </div>

      {mode === 'rail' && (
        <Button
          ref={railControlRef}
          type="button"
          variant="ghost"
          className="grid h-[52px] min-h-[52px] w-full shrink-0 grid-cols-[52px_minmax(0,1fr)] items-center justify-stretch gap-0 overflow-hidden whitespace-nowrap rounded-none px-[18px] text-xs font-semibold text-stone-600 hover:bg-stone-200/50 hover:text-stone-900 dark:text-stone-300 dark:hover:bg-background/10 dark:hover:text-white"
          onPointerDown={(event) => event.stopPropagation()}
          onTouchStart={(event) => event.stopPropagation()}
          onClick={() => (railExpanded ? collapseRail() : expandRail())}
          aria-label={railExpanded ? 'Collapse navigation' : 'Expand navigation'}
          aria-expanded={railExpanded}
          data-rail-control
        >
          <span className="flex h-[44px] w-[52px] items-center justify-center">
            {railExpanded
              ? <PanelLeftClose className="h-4 w-4" aria-hidden="true" />
              : <PanelLeftOpen className="h-4 w-4" aria-hidden="true" />}
          </span>
          <span
            data-navigation-label
            className={cn('min-w-0 whitespace-nowrap pl-3 text-left', !expanded && 'invisible')}
            aria-hidden={!expanded ? 'true' : undefined}
          >
            {railExpanded ? 'Collapse navigation' : 'Expand navigation'}
          </span>
        </Button>
      )}

      <nav
        className="admin-sidebar-main flex flex-1 flex-col overflow-x-hidden overflow-y-auto py-2"
        aria-label="Cockpits"
      >
        {visibleCategories.map((categoryId) => {
          const category = adminRoutes[categoryId];
          return (
            <AdminCockpitNavItem
              key={categoryId}
              categoryId={categoryId}
              category={category}
              canAccessSection={canAccessSection}
              isActive={activeCategoryId === categoryId}
              collapsed={!expanded}
              onClick={overlay ? onDismiss : undefined}
            />
          );
        })}
      </nav>

      <div className="shrink-0 border-t border-stone-300 py-1 dark:border-amber-600/30">
        <Button
          variant="ghost"
          onClick={() => window.open('https://owazamartialarts.com', '_blank')}
          className="grid h-[52px] min-h-[52px] w-full grid-cols-[52px_minmax(0,1fr)] items-center justify-stretch gap-0 overflow-hidden whitespace-nowrap rounded-none px-[18px] text-xs font-semibold text-stone-600 hover:bg-stone-200/50 hover:text-stone-900 dark:text-stone-300 dark:hover:bg-background/10 dark:hover:text-white"
          title="View public site"
          aria-label="View Site"
          data-footer-target="view-site"
        >
          <span className="flex h-[44px] w-[52px] items-center justify-center">
            <Home className="h-4 w-4 shrink-0" aria-hidden="true" />
          </span>
          <span
            data-navigation-label
            className={cn('min-w-0 whitespace-nowrap pl-3 text-left', !expanded && 'invisible')}
            aria-hidden={!expanded ? 'true' : undefined}
          >
            View Site
          </span>
        </Button>
        <Button
          variant="ghost"
          onClick={onLogout}
          className="grid h-[52px] min-h-[52px] w-full grid-cols-[52px_minmax(0,1fr)] items-center justify-stretch gap-0 overflow-hidden whitespace-nowrap rounded-none px-[18px] text-xs font-semibold text-stone-600 hover:bg-stone-200/50 hover:text-stone-900 dark:text-stone-300 dark:hover:bg-background/10 dark:hover:text-white"
          aria-label="Logout"
          data-footer-target="logout"
        >
          <span className="flex h-[44px] w-[52px] items-center justify-center">
            <LogOut className="h-4 w-4 shrink-0" aria-hidden="true" />
          </span>
          <span
            data-navigation-label
            className={cn('min-w-0 whitespace-nowrap pl-3 text-left', !expanded && 'invisible')}
            aria-hidden={!expanded ? 'true' : undefined}
          >
            Logout
          </span>
        </Button>

        <Button
          type="button"
          variant="ghost"
          onClick={openWhatsNew}
          className="relative grid h-[52px] min-h-[52px] w-full grid-cols-[52px_minmax(0,1fr)_auto] items-center justify-stretch gap-0 overflow-hidden whitespace-nowrap rounded-none px-[18px] text-xs font-semibold text-stone-600 hover:bg-stone-200/50 hover:text-stone-900 dark:text-stone-300 dark:hover:bg-background/10 dark:hover:text-white"
          title={`What's New · Build ${process.env.REACT_APP_GIT_COMMIT || 'local'}`}
          aria-label={`What's New, build ${process.env.REACT_APP_GIT_COMMIT?.slice(0, 7) || 'local'}${hasUnread ? ', unread updates' : ''}`}
          data-footer-target="whats-new"
        >
          <span className="relative flex h-[44px] w-[52px] shrink-0 items-center justify-center">
            <Sparkles className="h-4 w-4" aria-hidden="true" />
            {hasUnread && <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-background" aria-hidden="true" />}
          </span>
          <span
            data-navigation-label
            className={cn('min-w-0 whitespace-nowrap pl-3 text-left', !expanded && 'invisible')}
            aria-hidden={!expanded ? 'true' : undefined}
          >
            What&apos;s New
          </span>
          <span
            data-navigation-build
            className={cn(
              'pl-2 font-mono text-[9px] font-normal text-stone-500',
              !expanded && 'invisible',
            )}
            aria-hidden={!expanded ? 'true' : undefined}
          >
            {process.env.REACT_APP_GIT_COMMIT?.slice(0, 7) || 'local'}
          </span>
        </Button>
      </div>
    </aside>
  );
};

AdminSidebar.defaultProps = {
  overlay: false,
  onDismiss: null,
};

export default AdminSidebar;
